import { useState, useEffect, useRef,useCallback  } from 'react';
import { useNavigate } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_BE_API_URL;

const PlantAssistant = () => {
  const popupTimeoutRef = useRef(null);
  const [eyePosition, setEyePosition] = useState({ x: 0, y: 0 });
  const [showPopup, setShowPopup] = useState(false);
  const [currentMessage, setCurrentMessage] = useState(0);
  const [isBlinking, setIsBlinking] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [caringTips, setCaringTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messageType, setMessageType] = useState('notification'); // 'notification' or 'caringTip'
  const [isPopupHovered, setIsPopupHovered] = useState(false);
  const navigate = useNavigate();
  
  // Clear popup timeout
  const clearPopupTimeout = useCallback(() => {
    if (popupTimeoutRef.current) {
      clearTimeout(popupTimeoutRef.current);
      popupTimeoutRef.current = null;
    }
  }, []);

  // Set popup timeout
  const setPopupTimeout = useCallback(() => {
    clearPopupTimeout();
    popupTimeoutRef.current = setTimeout(() => {
      if (!isPopupHovered) {
        setShowPopup(false);
      }
    }, 8000);
  }, [clearPopupTimeout, isPopupHovered]);

  // Fetch weather recommendations (notifications) - UPDATED for new API format
  const fetchNotifications = async () => {
    try {
      const token = localStorage?.getItem('token'); 
      const response = await fetch(`${API_BASE_URL}/api/virtualAssis/pafs/get-weather-recommendations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setNotifications(data.notifications || []);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      // Updated fallback to match new format
      setNotifications([
       {
          title: "Flourish Your Garden with Okra",
          body: "Dear Respected Citizen, Okra grows well in this weather across Pakistan. Please plant it and contribute towards a greener country."
        },
        {
          title: "Enhance Pakistan with Date Palms", 
          body: "Dear Respected Citizen, Date Palm grows well in this weather across Pakistan. Please plant it and contribute towards a greener country."
        },
        {
          title: "Beautify Your Space with Bougainvillea",
          body: "Dear Respected Citizen, Bougainvillea grows well in this weather across Pakistan. Please plant it and contribute towards a greener country."
        }

      ]);
    }
  };

  // Fetch caring tips
  const fetchCaringTips = async () => {
    try {
      const token = localStorage?.getItem('token'); 
      const response = await fetch(`${API_BASE_URL}/api/virtualAssis/pafs/get-caring-tips-on-planted-species`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setCaringTips(data.caringTips || []);
    } catch (error) {
      console.error('Error fetching caring tips:', error);
      // Fallback to sample data
      setCaringTips([
       {
          species: "General Plantation",
          plantationIds: [],
          caringTips: "1. Water your plants regularly, but avoid overwatering. Check the soil moisture before watering.\n\n2. Choose a sunny spot for your plants, as most species grow better with sufficient sunlight.\n\n3. Keep the soil healthy by adding compost or natural fertilizers.\n\n4. Protect your plants from pests and diseases by checking them frequently.\n\n5. Contribute to a greener Pakistan by planting more trees and caring for them consistently."
        }

      ]);
    }
  };

  // Initialize data fetching
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchNotifications(), fetchCaringTips()]);
      setLoading(false);
    };
    loadData();
  }, []);

  // Handle caring tip button click - Mock function for demo
  const handleCaringTipClick = (plantationIds) => {
     navigate('/plantations/array-activity', { state: { ids: plantationIds } });
    //alert(`Would navigate to plantations page with ${plantationIds.length} plantation(s)`);
  };

  // Eye tracking effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isMinimized) return;
      
      const assistant = document.querySelector('.plant-assistant');
      if (assistant) {
        const rect = assistant.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        
        const deltaX = e.clientX - centerX;
        const deltaY = e.clientY - centerY;
        
        const maxMove = 8;
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        const normalizedX = (deltaX / distance) * Math.min(distance / 50, maxMove);
        const normalizedY = (deltaY / distance) * Math.min(distance / 50, maxMove);
        
        setEyePosition({ 
          x: isNaN(normalizedX) ? 0 : normalizedX, 
          y: isNaN(normalizedY) ? 0 : normalizedY 
        });
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, [isMinimized]);

  // Blinking animation
  useEffect(() => {
    const blinkInterval = setInterval(() => {
      setIsBlinking(true);
      setTimeout(() => setIsBlinking(false), 150);
    }, 3000 + Math.random() * 2000);

    return () => clearInterval(blinkInterval);
  }, []);

  // Auto popup messages with hover support
  useEffect(() => {
    if (loading) return;
    
    const messageInterval = setInterval(() => {
      if (!showPopup && !isMinimized) {
        // Alternate between notifications and caring tips
        const showNotification = Math.random() > 0.5;
        
        if (showNotification && notifications.length > 0) {
          setMessageType('notification');
          setCurrentMessage(Math.floor(Math.random() * notifications.length));
        } else if (caringTips.length > 0) {
          setMessageType('caringTip');
          setCurrentMessage(Math.floor(Math.random() * caringTips.length));
        }
        
        setShowPopup(true);
        setPopupTimeout();
      }
    }, 10000);

    return () => {
      clearInterval(messageInterval);
      clearPopupTimeout();
    };
  }, [showPopup, isMinimized, notifications, caringTips, loading, isPopupHovered, setPopupTimeout, clearPopupTimeout]);

  // Handle popup hover state changes
  useEffect(() => {
    if (showPopup) {
      if (isPopupHovered) {
        clearPopupTimeout();
      } else {
        setPopupTimeout();
      }
    }
  }, [isPopupHovered, showPopup, setPopupTimeout, clearPopupTimeout]);

  const handleAssistantClick = () => {
    if (!showPopup && !isMinimized && !loading) {
      // Cycle through all messages
      const totalMessages = notifications.length + caringTips.length;
      if (totalMessages > 0) {
        const nextIndex = (currentMessage + 1) % totalMessages;
        if (nextIndex < notifications.length) {
          setMessageType('notification');
          setCurrentMessage(nextIndex);
        } else {
          setMessageType('caringTip');
          setCurrentMessage(nextIndex - notifications.length);
        }
        setShowPopup(true);
        setPopupTimeout();
      }
    }
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
    setShowPopup(false);
    clearPopupTimeout();
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    clearPopupTimeout();
  };

  // Handle popup mouse enter
  const handlePopupMouseEnter = () => {
    setIsPopupHovered(true);
  };

  // Handle popup mouse leave
  const handlePopupMouseLeave = () => {
    setIsPopupHovered(false);
  };

  const getCurrentMessage = () => {
    if (messageType === 'notification' && notifications[currentMessage]) {
      const notification = notifications[currentMessage];
      
      let content = notification.body;
      
      // Safely handle if content is an object
      if (typeof content === 'object' && content !== null) {
         content = content.body || content.description || JSON.stringify(content);
      }
      
      // Handle empty content
      if (!content || (typeof content === 'string' && content.trim() === '')) {
         content = "Check weather details for more information.";
      }

      return {
        title: `🌱 ${notification.title}`,
        content: content,
        type: 'notification'
      };
    } else if (messageType === 'caringTip' && caringTips[currentMessage]) {
      const tip = caringTips[currentMessage];
      
      let formattedContent = "No specific tips available for this plant.";
      
      // Handle the nested structure: tip.caringTips (object) -> caringTips (array) -> {title, body}
      if (tip.caringTips) {
        if (typeof tip.caringTips === 'object') {
           // Check for the inner array "caringTips"
           if (tip.caringTips.caringTips && Array.isArray(tip.caringTips.caringTips)) {
               formattedContent = tip.caringTips.caringTips.map(t => 
                 `🔹 ${t.title || 'Tip'}\n${t.body || ''}`
               ).join('\n\n');
           } 
           // If the outer tip.caringTips is the array itself (backward compatibility)
           else if (Array.isArray(tip.caringTips)) {
               formattedContent = tip.caringTips.map(t => 
                 typeof t === 'object' ? `🔹 ${t.title || 'Tip'}\n${t.body || ''}` : t
               ).join('\n\n');
           }
           // Fallback for object without known structure
           else {
               formattedContent = JSON.stringify(tip.caringTips);
           }
        } else if (typeof tip.caringTips === 'string') {
            formattedContent = tip.caringTips;
        }
      }

      return {
        title: `🌿 ${tip.species} Care Tips`,
        content: formattedContent,
        type: 'caringTip',
        plantationIds: tip.plantationIds
      };
    }
    return null;
  };

  const currentMsg = getCurrentMessage();

  if (loading) {
    return (
      <div 
        className="plant-assistant"
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          width: '100px',
          height: '100px',
          zIndex: 2000,
          filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.15))'
        }}
      >
        <div style={{
          width: '100%',
          height: '100%',
          backgroundColor: '#f3f4f6',
          borderRadius: '20px',
          border: '3px solid #d1d5db',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '12px',
          color: '#6b7280'
        }}>
          Loading...
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Popup Message */}
      {showPopup && !isMinimized && currentMsg && (
        <div 
          className="popup-message"
          style={{
            position: 'fixed',
            bottom: '140px',
            right: '30px',
            backgroundColor: '#ffffff',
            border: '2px solid #4ade80',
            borderRadius: '16px',
            padding: '16px',
            maxWidth: '320px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
            zIndex: 1001,
            animation: 'slideUp 0.3s ease-out'
          }}
          onMouseEnter={handlePopupMouseEnter}
          onMouseLeave={handlePopupMouseLeave}
        >
          {/* Fixed header with proper positioning */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start',
            marginBottom: '8px',
            gap: '8px' // Add gap to prevent overlap
          }}>
            <h4 style={{ 
              margin: '0', 
              color: '#16a34a', 
              fontSize: '14px',
              fontWeight: '600',
              lineHeight: '1.2',
              flex: '1', // Allow title to take available space
              paddingRight: '4px' // Small padding to ensure spacing
            }}>
              {currentMsg.title}
            </h4>
            <button
              onClick={handleClosePopup}
              style={{
                background: 'none',
                border: 'none',
                color: '#9ca3af',
                fontSize: '18px',
                cursor: 'pointer',
                padding: '0',
                lineHeight: '1',
                width: '20px', // Fixed width
                height: '20px', // Fixed height
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0, // Prevent shrinking
                borderRadius: '4px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f3f4f6';
                e.target.style.color = '#6b7280';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#9ca3af';
              }}
            >
              ×
            </button>
          </div>
          <p style={{ 
            margin: '0 0 12px 0', 
            color: '#374151', 
            fontSize: '13px',
            lineHeight: '1.4',
            whiteSpace: 'pre-line'
          }}>
            {currentMsg.content}
          </p>
          
          {/* Show button for caring tips with plantation IDs */}
          {currentMsg.type === 'caringTip' && currentMsg.plantationIds && currentMsg.plantationIds.length > 0 && (
            <button
              onClick={() => handleCaringTipClick(currentMsg.plantationIds)}
              style={{
                backgroundColor: '#4ade80',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 12px',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease',
                width: '100%'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#22c55e'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#4ade80'}
            >
              View Plantations ({currentMsg.plantationIds.length})
            </button>
          )}
          
          <div style={{
            position: 'absolute',
            bottom: '-8px',
            right: '40px',
            width: '0',
            height: '0',
            borderLeft: '8px solid transparent',
            borderRight: '8px solid transparent',
            borderTop: '8px solid #4ade80'
          }}></div>
        </div>
      )}

      {/* Plant Assistant Widget */}
      <div 
        className="plant-assistant"
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          width: isMinimized ? '60px' : '100px',
          height: isMinimized ? '60px' : '100px',
          transition: 'all 0.3s ease',
          zIndex: 2000,
          cursor: 'pointer',
          filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.15))'
        }}
        onClick={handleAssistantClick}
      >
        {/* Main Plant Container */}
        <div style={{
          width: '100%',
          height: '100%',
          backgroundColor: '#f3f4f6',
          borderRadius: isMinimized ? '50%' : '20px',
          border: '3px solid #d1d5db',
          position: 'relative',
          overflow: 'visible',
          transition: 'all 0.3s ease',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}>
          
          {/* Plant Leaves */}
          {!isMinimized && (
            <div
              style={{
                position: "absolute",
                top: "-40px",
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 1,
              }}
            >
              {/* Multiple realistic leaves */}
              {[...Array(6)].map((_, i) => {
                const rotation = i * 30 - 75;
                return (
                  <div
                    key={i}
                    style={{
                      position: "absolute",
                      width: "20px",
                      height: "35px",
                      background:
                        "linear-gradient(135deg, #4ade80 0%, #22c55e 30%, #16a34a 70%, #15803d 100%)",
                      borderRadius: "50% 20% 50% 20%",
                      transform: `rotate(${rotation}deg) translateY(-${15 + i * 2}px)`,
                      transformOrigin: "bottom center",
                      animation: `gentle-sway 3s ease-in-out infinite ${i * 0.2}s`,
                      boxShadow: `
                        inset 0 2px 6px rgba(255,255,255,0.3),
                        inset 0 -2px 6px rgba(0,0,0,0.2),
                        0 2px 8px rgba(34, 197, 94, 0.3)
                      `,
                      border: "1px solid rgba(22, 163, 74, 0.4)",
                      "--rotation": `${rotation}deg`,
                      "--translateY": `-${15 + i * 2}px`,
                    }}
                  >
                    {/* Central vein */}
                    <div
                      style={{
                        position: "absolute",
                        top: "10%",
                        left: "50%",
                        width: "1px",
                        height: "80%",
                        background:
                          "linear-gradient(to bottom, rgba(255,255,255,0.6) 0%, rgba(22, 163, 74, 0.8) 50%, transparent 100%)",
                        transform: "translateX(-50%)",
                        borderRadius: "0.5px",
                      }}
                    />

                    {/* Side veins */}
                    <div
                      style={{
                        position: "absolute",
                        top: "30%",
                        left: "30%",
                        width: "40%",
                        height: "0.5px",
                        background: "rgba(22, 163, 74, 0.4)",
                        transform: "rotate(20deg)",
                        transformOrigin: "left center",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        top: "30%",
                        right: "30%",
                        width: "40%",
                        height: "0.5px",
                        background: "rgba(22, 163, 74, 0.4)",
                        transform: "rotate(-20deg)",
                        transformOrigin: "right center",
                      }}
                    />

                    {/* Light reflection spot */}
                    <div
                      style={{
                        position: "absolute",
                        top: "25%",
                        left: "35%",
                        width: "6px",
                        height: "6px",
                        background:
                          "radial-gradient(circle, rgba(255,255,255,0.4) 0%, transparent 70%)",
                        borderRadius: "50%",
                        opacity: 0.8,
                      }}
                    />
                  </div>
                );
              })}
            </div>
          )}

          {/* Screen/Face Area */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: isMinimized ? '40px' : '70px',
            height: isMinimized ? '30px' : '50px',
            backgroundColor: '#1f2937',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: isMinimized ? '6px' : '12px'
          }}>
            
            {/* Eyes */}
            <div style={{
              width: isMinimized ? '8px' : '12px',
              height: isBlinking ? '2px' : (isMinimized ? '8px' : '12px'),
              backgroundColor: '#ffffff',
              borderRadius: '50%',
              transform: `translate(${eyePosition.x}px, ${eyePosition.y}px)`,
              transition: isBlinking ? 'height 0.1s ease' : 'transform 0.1s ease',
              boxShadow: '0 0 4px rgba(255,255,255,0.3)'
            }}></div>
            
            <div style={{
              width: isMinimized ? '8px' : '12px',
              height: isBlinking ? '2px' : (isMinimized ? '8px' : '12px'),
              backgroundColor: '#ffffff',
              borderRadius: '50%',
              transform: `translate(${eyePosition.x}px, ${eyePosition.y}px)`,
              transition: isBlinking ? 'height 0.1s ease' : 'transform 0.1s ease',
              boxShadow: '0 0 4px rgba(255,255,255,0.3)'
            }}></div>
          </div>

          {/* Small smile */}
          {!isMinimized && (
            <div style={{
              position: 'absolute',
              bottom: '15px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '20px',
              height: '10px',
              border: '2px solid #ef4444',
              borderTop: 'none',
              borderRadius: '0 0 20px 20px'
            }}></div>
          )}

          {/* Minimize/Maximize Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleMinimize();
            }}
            style={{
              position: 'absolute',
              top: '5px',
              right: '5px',
              width: '16px',
              height: '16px',
              backgroundColor: '#6b7280',
              border: 'none',
              borderRadius: '50%',
              color: '#ffffff',
              fontSize: '10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0.7,
              transition: 'opacity 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.opacity = '1'}
            onMouseLeave={(e) => e.target.style.opacity = '0.7'}
          >
            {isMinimized ? '+' : '−'}
          </button>
        </div>
      </div>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes slideUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes gentle-sway {
          0%, 100% {
            transform: rotate(var(--rotation)) translateY(var(--translateY)) rotate(0deg);
          }
          50% {
            transform: rotate(var(--rotation)) translateY(var(--translateY)) rotate(5deg);
          }
        }

        .plant-assistant:hover {
          transform: scale(1.05);
        }

        .popup-message:hover {
          box-shadow: 0 15px 35px rgba(0,0,0,0.2);
        }
      `}</style>
    </>
  );
};

export default PlantAssistant